import { useIsMutating } from '@tanstack/react-query'
import { TOP_UP_KEY, type TopUpOptions, type TopUpResult, useTopUp } from '@/shared/api/leads'
import { BrandLoader } from '@/shared/white-label/BrandLoader'
import { Button } from '@/ui'
import { READ_ONLY_TITLE, useCanEdit } from '@/shared/write-access'

/**
 * Pull the caller's queue back up to target from the shared pool.
 *
 * Focus shows only what a seller owns, so an empty queue is a dead end rather
 * than a lull: nothing arrives on its own except the one automatic top-up when
 * Focus mounts. This is that same pull, on demand — the whole screen when the
 * queue is empty.
 *
 * Every outcome gets its own sentence. "Nothing arrived" has three different
 * causes — an empty pool, a queue that is already full, and colleagues holding
 * the rows for an instant — and only the first one means stop asking.
 */
interface Props {
  variant: 'empty'
  /** The secondary way out, into the full pipeline. */
  onSwitch?: () => void
  onClaimed?: (ids: string[]) => void
}

function outcomeMessage(res: TopUpResult): string {
  const n = res.claimed.length
  if (n > 0) return `${n} ny${n === 1 ? '' : 'a'} lead${n === 1 ? '' : 's'} i din kö.`
  switch (res.stopReason) {
    case 'ceiling':
      // The only outcome the seller can act on themselves, so it says how.
      return `Du håller i ${res.openOwned} av ${res.maxOpen} leads. `
        + 'Bearbeta några, eller lämna tillbaka de du inte tänker ringa, för att ta fler.'
    case 'poolEmpty':
      return 'Poolen är tom — alla leads inom ditt område är redan tilldelade.'
    case 'autoOff':
      return 'Leads delas ut av din chef just nu, inte automatiskt.'
    case 'contended':
      return 'Kollegor tog från poolen i samma stund. Försök igen.'
    default:
      return `Du har redan ${res.owned} leads — kön fylls på till ${res.target}.`
  }
}

export default function GetMoreLeads({ onSwitch, onClaimed }: Props) {
  const topUp = useTopUp()
  const canEdit = useCanEdit()
  // The automatic top-up on opening Focus, or this button's. Leads it pulls
  // are spliced straight into the queue, so the loader hands over to the
  // first lead with nothing in between. The silent background refills do not
  // count: they tick on a timer and would blink the empty state.
  const refilling = useIsMutating({
    mutationKey: [TOP_UP_KEY],
    predicate: m => !(m.state.variables as TopUpOptions)?.silent,
  }) > 0

  const message = topUp.isError
    ? (topUp.error as Error).message || 'Kunde inte nå poolen.'
    : topUp.data
      ? outcomeMessage(topUp.data)
      : null

  const run = () =>
    topUp.mutate(undefined, {
      onSuccess: res => {
        if (res.claimed.length > 0) onClaimed?.(res.claimed.map(l => l.id))
      },
    })

  // Stays mounted while it loads, so the outcome of this button's pull is
  // still here to say why nothing arrived.
  if (refilling) return <BrandLoader label="Hämtar leads" className="h-full" />

  return (
    <div className="grid place-content-center text-center px-6 py-20 h-full">
      <h2 className="font-display text-[32px] font-medium tracking-tighter mt-0 mb-1.5 text-ink-1000">
        Din kö är tom
      </h2>
      <p className="text-ink-500 mt-0 mb-5">
        Focus visar bara leads som är tilldelade dig. Ta en omgång från den delade poolen för att fortsätta ringa.
      </p>
      {message && (
        <p className="text-ink-700 mt-0 mb-5" role="status">
          {message}
        </p>
      )}
      <div className="flex gap-2.5 justify-center">
        <Button
          variant="primary"
          onClick={run}
          disabled={!canEdit}
          title={canEdit ? undefined : READ_ONLY_TITLE}
        >
          Hämta fler leads
        </Button>
        {onSwitch && (
          <Button onClick={onSwitch}>
            Öppna listan
          </Button>
        )}
      </div>
    </div>
  )
}
